import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {
  ALL_ARCHIVED_PURPOSE_TEXT,
  NOT_ARCHIVED_PURPOSE_STRING,
  NOT_ARCHIVED_PURPOSE_TEXT,
} from 'archive/ArchiveConstants';
import * as ArchivePurposesUtil from 'archive/ArchivePurposesUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import {listToReadable} from 'util/NameUtil';

interface ArchivePurposeDropdownProps {
  showLabel?: boolean;
  itemIds: Array<string | number>;
  onChangePurposes: (purposes: Array<string>) => void;
  selectedPurposes: Array<string>;
}

export default class ArchivePurposeDropdown extends React.PureComponent<ArchivePurposeDropdownProps, {}> {
  static propTypes = {
    itemIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
    onChangePurposes: PropTypes.func.isRequired,
    selectedPurposes: PropTypes.arrayOf(PropTypes.string).isRequired,
  };

  _handleSelectPurpose = (purpose: string | Array<string>): void => {
    const purposes = Array.isArray(purpose) ? purpose : [purpose];
    const alreadySelected = purposes.every((eachPurpose) =>
      this.props.selectedPurposes.includes(eachPurpose)
    );
    if (alreadySelected) {
      this.props.onChangePurposes(_.difference(this.props.selectedPurposes, purposes));
    } else {
      this.props.onChangePurposes(_.union(this.props.selectedPurposes, purposes));
    }
  };

  _getPurposesText(): string {
    if (this.props.selectedPurposes.length) {
      const nonVisiblePurposes = _.difference(
        this.props.selectedPurposes,
        ArchivePurposesUtil.getVisibleArchivePurposes(this.props.itemIds)
      );
      // eslint-disable-next-line no-restricted-syntax
      const purposeTexts = _.uniq(
        _.compact(
          this.props.selectedPurposes.map((purpose) => {
            if (purpose === NOT_ARCHIVED_PURPOSE_STRING) {
              return NOT_ARCHIVED_PURPOSE_TEXT;
            } else if (nonVisiblePurposes.includes(purpose)) {
              return ALL_ARCHIVED_PURPOSE_TEXT;
            } else {
              return purpose;
            }
          })
        )
      );
      return this.props.showLabel
        ? listToReadable(purposeTexts)
        : `Filtering by: ${listToReadable(purposeTexts)}`;
    }
    return this.props.showLabel ? 'Archive reason ...' : 'Filter by archive reason...';
  }

  private renderDropdown(): JSX.Element {
    const visibleArchivePurposes = ArchivePurposesUtil.getVisibleArchivePurposes(this.props.itemIds);
    const allArchivePurposes = ArchivePurposesUtil.getAllArchivePurposes();
    return (
      <SelectDropdown
        className="archivePurposeDropdown"
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.MEDIUM}
            value={this.props.selectedPurposes}
            isMulti={true}
          >
            <Section>
              <Option
                value={NOT_ARCHIVED_PURPOSE_STRING}
                onSelect={() => {
                  this._handleSelectPurpose(NOT_ARCHIVED_PURPOSE_STRING);
                }}
                dataTestAttrs={{component: 'ArchivePurposeItem', key: NOT_ARCHIVED_PURPOSE_STRING}}
              >
                {NOT_ARCHIVED_PURPOSE_TEXT}
              </Option>
            </Section>
            {visibleArchivePurposes.length ? (
              <Section>
                {visibleArchivePurposes.map((purpose: string) => (
                  <Option key={purpose} value={purpose} onSelect={this._handleSelectPurpose}>
                    {purpose}
                  </Option>
                ))}
              </Section>
            ) : (
              <Section>
                <Option
                  value={allArchivePurposes}
                  onSelect={() => {
                    this._handleSelectPurpose(allArchivePurposes);
                  }}
                >
                  {ALL_ARCHIVED_PURPOSE_TEXT}
                </Option>
              </Section>
            )}
          </Menu>
        }
        displayName={this._getPurposesText()}
      />
    );
  }

  override render(): JSX.Element {
    return this.props.showLabel ? (
      <FormGroup>
        <FieldLabel label="Archive status" />
        {this.renderDropdown()}
      </FormGroup>
    ) : (
      this.renderDropdown()
    );
  }
}
